import { gql, useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { Input, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { FETCH_ALL_LINKS } from "./index";

const SAVE_LINK = gql`
  mutation SAVE_LINK(
    $name: String!
    $originLink: String!
    $slug: String!
    $description: String
    $id: ID
  ) {
    saveLink(
      name: $name
      originLink: $originLink
      slug: $slug
      description: $description
      id: $id
    ) {
      link {
        id
        name
        originLink
        slug
        shorternLink
        description
      }
    }
  }
`;

const DELETE_LINK = gql`
  mutation DELETE_LINK($id: ID!) {
    deleteLink(id: $id) {
      deleted
    }
  }
`;

const DeleteButton = ({ id, didDelete }) => {
  const [deleteLink, deleteLinkRes] = useMutation(DELETE_LINK, {
    variables: { id },
    onCompleted: () => {
      Alert("success", "Link Deleted.");
      didDelete();
    },
    onError: (error) => Alert("error", error.message),
    refetchQueries: [{ query: FETCH_ALL_LINKS }],
  });
  return (
    <Button
      danger
      loading={deleteLinkRes.loading}
      onClick={() => {
        if (window.confirm("Are you sure to delete this link?")) {
          deleteLink();
        }
      }}
    >
      Delete Link
    </Button>
  );
};

const LinkForm = ({ link, hide }) => {
  const [name, setName] = useState(link ? link.name : "");

  const generateRandomSlug = (length = 6) => {
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let out = "";
    for (let i = 0; i < length; i++) {
      out += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return out;
  };

  const generateUniqueShort = () => {
    // Combine random chars with a timestamp fragment to make collisions extremely unlikely.
    return `${generateRandomSlug(5)}${Date.now().toString(36).slice(-4)}`;
  };

  const initialShortLink =
    link && link.shorternLink ? link.shorternLink : generateUniqueShort();

  const [originLink, setOriginLink] = useState(link ? link.originLink : "");
  const [slug, setSlug] = useState(link ? link.slug : initialShortLink);
  const [description, setDescription] = useState(link ? link.description : "");
  const [createLink, createLinkRes] = useMutation(SAVE_LINK, {
    variables: {
      id: link ? link.id : null,
      name,
      originLink,
      slug,
      description,
    },
    onCompleted: hide,
    onError: (error) => Alert("error", error.message),
    refetchQueries: [{ query: FETCH_ALL_LINKS }],
  });

  const isSlugValid = slug.match(/^[a-zA-Z0-9-_]+$/);

  return (
    <div className="">
      <div className="flex items-baseline">
        <label className="w-32 mr-4">Name: </label>
        <Input
          placeholder="Give a name for this link, for example: Moon Product Page."
          className="flex-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="mt-4">
        <div className="flex items-baseline ">
          <label className="w-32 mr-4">Shorten Link: </label>
          <div className="flex flex-1 items-baseline">
            <div>{process.env.REACT_APP_SHORT_LINK_ORIGIN}/</div>
            <Input
              placeholder="whatever"
              className="flex-1 ml-2"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
        </div>
        {slug && !isSlugValid ? (
          <div className="text-red-600 text-sm ml-36 mt-1">
            Only letters, numbers, hyphen and underscore are allowed.
          </div>
        ) : null}
      </div>
      <div className="flex items-baseline mt-4">
        <label className="w-32 mr-4">Origin Link: </label>
        <Input
          className="flex-1"
          value={originLink}
          onChange={(e) => setOriginLink(e.target.value)}
        />
      </div>
      <div className="flex items-start mt-4">
        <label className="w-32 mr-4">Description: </label>
        <Text
          placeholder="Optional, write down anything in case you need to know later.
               "
          className="flex-1"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className="flex items-center mt-4">
        <label className="w-32 mr-4"></label>
        <Button
          loading={createLinkRes.loading}
          disabled={createLinkRes.loading || !name || !slug || !isSlugValid}
          onClick={createLink}
        >
          Save
        </Button>
        {link ? (
          <div className="ml-4">
            <DeleteButton id={link.id} didDelete={hide} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default LinkForm;
